import { useState, createContext, useEffect } from 'react'
import { useMatch } from 'react-router-dom'

export const ProgramContext = createContext()

export const ProgramContextProvider = ({ children }) => {

    // URL PARAMS

    const programIdMatch = useMatch({ path: '/program/:urlProgramId', end: false })
    const programId = programIdMatch?.params?.urlProgramId ?? null

    // MESO VOLUME CONFIG SUB CONTEXT

    const [mesoVolumeConfig, setMesoVolumeConfig] = useState(null)
    const [mesoVolumeConfigDraft, setMesoVolumeConfigDraft] = useState(null)
    const [mesoVolumeConfigDraftIsSaving, setMesoVolumeConfigDraftIsSaving] = useState(false)
    const [mesoVolumeConfigs, setMesoVolumeConfigs] = useState(null)

    const MesoVolumeConfigSubContextData = {
        mesoVolumeConfig,
        mesoVolumeConfigs,
        mesoVolumeConfigDraft,
        mesoVolumeConfigDraftIsSaving,
        setMesoVolumeConfig,
        setMesoVolumeConfigDraft,
    }

    // MICRO VOLUME CONFIG SUB CONTEXT

    const GetMicroVolumeConfigRepsCount = (microVolumeConfig) => {
        var repsCount = 0

        for (const workoutConfig of microVolumeConfig.workoutConfigs) {
            for (const workoutPhaseConfig of workoutConfig.workoutPhaseConfigs) {
                var workoutPhaseSets = workoutPhaseConfig.sets
                for (const workoutSlotConfig of workoutPhaseConfig.workoutSlotConfigs) {
                    repsCount += (workoutPhaseSets * workoutSlotConfig.bilateralReps)
                }
            }
        }

        return repsCount
    }

    const GetMicroVolumeConfigSetCount = (microVolumeConfig) => {
        var setsCount = 0

        for (const workoutConfig of microVolumeConfig.workoutConfigs) {
            for (const workoutPhaseConfig of workoutConfig.workoutPhaseConfigs) {
                setsCount += workoutPhaseConfig.sets
            }
        }

        return setsCount
    }

    const HandleMicroVolumeConfigDraftChange = (microVolumeConfigIndex, key, value) => {
        if (microVolumeConfigIndex === null) return

        switch (key) {
            case 'useTaper':
                setMesoVolumeConfigDraft((previous) => ({
                    ...previous,
                    microVolumes: previous.microVolumes.map((microVolume, index) => (
                        index === microVolumeConfigIndex
                            ? {
                                ...microVolume,
                                useTaper: value,
                                ...(value === 0
                                    ? {
                                        taperLevel: 'taper-none',
                                        taperDampener: 1,
                                        exerciseRegressionLevel: 0
                                    }
                                    : {})
                            }
                            : microVolume
                    )),
                }))
                break
            case 'microVolumeStimulusTypeId':
            case 'taperLevel':
            case 'taperDampener':
            case 'taperType':
            case 'exerciseRegressionLevel':
                setMesoVolumeConfigDraft((previous) => ({
                    ...previous,
                    microVolumes: previous.microVolumes.map((microVolume, index) => (
                        index === microVolumeConfigIndex
                            ? { ...microVolume, [key]: value }
                            : microVolume
                    )),
                }))
                break
            case 'unilateralSeconds':
            case 'bilateralSeconds': 
            {
                const { workoutConfigIndex, seconds } = value

                if (seconds < 1) return

                setMesoVolumeConfigDraft((previous) => {
                    const newMicroVolumes = structuredClone(previous.microVolumes)
                    newMicroVolumes[microVolumeConfigIndex].workoutConfigs[workoutConfigIndex][key] = seconds
                    return { ...previous, microVolumes: newMicroVolumes }
                })
                break
            }
            case 'mediumVolumePlyometricUnilateralRepsOverride':
            case 'mediumVolumePlyometricBilateralRepsOverride':
                {
                    const { workoutConfigIndex, reps } = value
    
                    if (reps < 1) return
    
                    setMesoVolumeConfigDraft((previous) => {
                        const newMicroVolumes = structuredClone(previous.microVolumes)
                        newMicroVolumes[microVolumeConfigIndex].workoutConfigs[workoutConfigIndex][key] = reps
                        return { ...previous, microVolumes: newMicroVolumes }
                    })
                    break
                }
            case 'sets': {
                const { workoutConfigIndex, workoutPhaseIndex, sets } = value

                if (sets < 1) return

                setMesoVolumeConfigDraft((previous) => {
                    const newMicroVolumes = structuredClone(previous.microVolumes)
                    newMicroVolumes[microVolumeConfigIndex].workoutConfigs[workoutConfigIndex].workoutPhaseConfigs[workoutPhaseIndex].sets = sets
                    return { ...previous, microVolumes: newMicroVolumes }
                })
                break
            }
        }

    }

    const __GetNewMicroVolumeConfig = () => {
        var newMicroVolumeConfig = {
            _animationUUID: crypto.randomUUID(),
            microVolumeStimulusTypeId: parseInt(Object.keys(programMetadata?.microVolumeStimulusTypeHashMap)[0]) ?? null,
            useTaper: 0,
            taperLevel: 'taper-none',
            taperDampener: 1.00,
            taperType: 'program',
            exerciseRegressionLevel: 0
        }

        var newWorkoutConfigs = structuredClone(programMetadata?.workoutConfigs)

        for (var paramWorkoutConfig of newWorkoutConfigs) {

            // Default WorkoutNanoVolume values

            paramWorkoutConfig.lowVolumePlyometricUnilateralRepsOverride = 16
            paramWorkoutConfig.lowVolumePlyometricBilateralRepsOverride = 16
            paramWorkoutConfig.mediumVolumePlyometricUnilateralRepsOverride = 16
            paramWorkoutConfig.mediumVolumePlyometricBilateralRepsOverride = 16
            paramWorkoutConfig.highVolumePlyometricUnilateralRepsOverride = 16
            paramWorkoutConfig.highVolumePlyometricBilateralRepsOverride = 16
            paramWorkoutConfig.unilateralSeconds = 60
            paramWorkoutConfig.bilateralSeconds = 60

            for (var workoutPhase of paramWorkoutConfig.workoutPhaseConfigs) {

                // Hard code specific default values for warm-up phase

                if (workoutPhase.workoutPhaseConfigKey === 'warm-up') {

                    workoutPhase.sets = 1;

                    for (var workoutSlot of workoutPhase.workoutSlotConfigs) {
                        workoutSlot.unilateralReps = 5
                        workoutSlot.bilateralReps = 10
                    }

                    continue;
                }

                // All other phase defaults

                workoutPhase.sets = 2;

                for (var workoutSlot of workoutPhase.workoutSlotConfigs) {
                    workoutSlot.unilateralReps = 10
                    workoutSlot.bilateralReps = 10
                }
            }
        }

        newMicroVolumeConfig.workoutConfigs = newWorkoutConfigs

        return newMicroVolumeConfig
    }

    const MicroVolumeConfigSubContextData = {
        GetMicroVolumeConfigRepsCount,
        GetMicroVolumeConfigSetCount,
        HandleMicroVolumeConfigDraftChange
    }

    // NANO VOLUME WORKOUT SLOT CONFIG SUB CONTEXT

    const HandleNanoVolumeWorkoutSlotConfigDraftChange = (microVolumeConfigIndex, workoutConfigIndex, workoutPhaseIndex, workoutSlotIndex, key, value) => {
        if (microVolumeConfigIndex === null) return

        switch (key) {
            case 'unilateralReps':
            case 'bilateralReps':
            case 'plyometricOverrideUnilateralReps':
            case 'plyometricOverrideBilateralReps':
            case 'unilateralSeconds':
            case 'bilateralSeconds':
                setMesoVolumeConfigDraft((previous) => {
                    const newMicroVolumes = structuredClone(previous.microVolumes)
                    newMicroVolumes[microVolumeConfigIndex].workoutConfigs[workoutConfigIndex].workoutPhaseConfigs[workoutPhaseIndex].workoutSlotConfigs[workoutSlotIndex][key] = value
                    return { ...previous, microVolumes: newMicroVolumes }
                })
                break
        }
    }

    // TODO: Rename to WorkoutSlotNanoVolumeSubContextData
    const NanoVolumeWorkoutSlotConfigSubContextData = {
        HandleNanoVolumeWorkoutSlotConfigDraftChange
    }

    // PROGRAM METADATA SUB CONTEXT

    const [programMetadata, setProgramMetadata] = useState(null)

    // FULL CONTEXT

    const FullContextData = {
        MesoVolumeConfigSubContextData,
        MicroVolumeConfigSubContextData,
        NanoVolumeWorkoutSlotConfigSubContextData
    }

    // RESET CONTEXT

    useEffect(() => { 
        setProgramMetadata(null)
        // setWorkoutConfig(null)
        // setWorkoutConfigs(null)
    }, [programId])

    return (
        <ProgramContext.Provider value={FullContextData}>
            {children}
        </ProgramContext.Provider>
    )
}
